// Påminnelse i appen när ett delegeringsbrev (Dok 52) håller på att löpa ut.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BellRing } from "lucide-react";
import {
  acknowledgeDelegationReminder,
  fetchDelegationReminders,
  type DelegationReminder,
} from "@/lib/basProcess/delegationExpiry";

const levelText = (r: DelegationReminder) =>
  r.days < 0
    ? `gick ut för ${Math.abs(r.days)} dagar sedan`
    : r.days === 0
      ? "går ut i dag"
      : `går ut om ${r.days} dagar`;

export const DelegationExpiryBanner: React.FC<{ projectId: string; canManage?: boolean }> = ({
  projectId,
  canManage = true,
}) => {
  const [reminders, setReminders] = useState<DelegationReminder[]>([]);

  const load = useCallback(async () => {
    setReminders(await fetchDelegationReminders(projectId));
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const ack = async (id: string) => {
    const { error } = await acknowledgeDelegationReminder(id);
    if (error) return toast.error("Kunde inte kvittera påminnelsen");
    toast.success("Påminnelsen är kvitterad");
    void load();
  };

  const open = reminders.filter((r) => !r.acknowledged);
  if (open.length === 0) return null;

  const critical = open.some((r) => r.level === "expired" || r.level === "critical");

  return (
    <div
      className={`rounded-xl border p-4 ${
        critical ? "border-destructive/50 bg-destructive/5" : "border-amber-500/40 bg-amber-500/5"
      }`}
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <BellRing className="h-4 w-4" />
        Delegeringsbrev behöver förnyas
      </p>
      <ul className="mt-2 space-y-2">
        {open.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{r.toName}</span>
              <Badge variant={r.level === "expired" || r.level === "critical" ? "destructive" : "outline"}>
                {levelText(r)}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Gäller till {new Date(r.validTo).toLocaleDateString("sv-SE")}
              </span>
            </span>
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => ack(r.id)}>
                Kvittera
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DelegationExpiryBanner;
